/**
 * TriggersDiffView — chip view for `propose_edit_skill(field='skill_triggers')`.
 *
 * The AoA sends skill trigger keywords as a JSON array (string or array),
 * and the plain DiffView would render that as raw JSON — hostile UX for a
 * keyword list. This view mirrors the visual grammar of
 * AllowedModelsDiffView / OutputFormatsDiffView: ADDED keywords get the
 * green-tinted chip, REMOVED ones the dimmed strike-through chip, KEPT
 * ones stay neutral — the same pill look the Edit-Skill page uses for
 * its trigger tags. ProposalCard falls back to the plain diff whenever
 * either side fails to parse as a list of strings (see parseTriggerList).
 */

import React, { useMemo } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import './AllowedModelsDiffView.css';

/**
 * Normalize a `skill_triggers` value into a clean string list.
 * Accepts a real array or a JSON-encoded array string; `null`/`undefined`/
 * empty string mean "no triggers". Returns `null` when the shape is not a
 * list of strings — the caller then falls back to the plain DiffView
 * instead of guessing.
 */
export function parseTriggerList(value: unknown): string[] | null {
  if (value === null || value === undefined) return [];
  let parsed: unknown = value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(parsed)) return null;
  const out: string[] = [];
  for (const item of parsed) {
    if (typeof item !== 'string') return null;
    const keyword = item.trim();
    if (keyword) out.push(keyword);
  }
  return out;
}

interface Props {
  /** Current keywords (parsed from proposal.old_value). */
  before: string[];
  /** Proposed keywords (parsed from proposal.new_value). */
  after: string[];
}

export const TriggersDiffView: React.FC<Props> = ({ before, after }) => {
  const { t } = useLanguage();

  const { kept, added, removed } = useMemo(() => {
    const beforeSet = new Set(before);
    const afterSet = new Set(after);
    return {
      kept: after.filter((k) => beforeSet.has(k)),
      added: after.filter((k) => !beforeSet.has(k)),
      removed: before.filter((k) => !afterSet.has(k)),
    };
  }, [before, after]);

  const renderChip = (keyword: string, variant: 'kept' | 'added' | 'removed') => (
    <span
      key={`${variant}-${keyword}`}
      className={`aoa-allowed-chip aoa-allowed-chip--${variant}`}
      title={keyword}
    >
      <span className="aoa-allowed-chip__label">{keyword}</span>
    </span>
  );

  return (
    <div className="aoa-allowed-models" data-testid="aoa-triggers-diff">
      <div className="aoa-allowed-models__section">
        <div className="aoa-allowed-models__heading">
          {t('metaAgent.proposal.triggers.heading')}
        </div>
        {after.length === 0 ? (
          <div className="aoa-allowed-models__chips">
            <span className="aoa-allowed-chip aoa-allowed-chip--all">
              <span className="aoa-allowed-chip__label">
                {t('metaAgent.proposal.triggers.noneLabel')}
              </span>
            </span>
          </div>
        ) : (
          <div className="aoa-allowed-models__chips">
            {kept.map((k) => renderChip(k, 'kept'))}
            {added.map((k) => renderChip(k, 'added'))}
          </div>
        )}
      </div>

      {removed.length > 0 && (
        <div className="aoa-allowed-models__section">
          <div className="aoa-allowed-models__heading aoa-allowed-models__heading--removed">
            {t('metaAgent.proposal.triggers.removedHeading')}
          </div>
          <div className="aoa-allowed-models__chips">
            {removed.map((k) => renderChip(k, 'removed'))}
          </div>
        </div>
      )}
    </div>
  );
};
